import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Send, Trash2, Undo2, Pencil, Image as ImageIcon,
  Plus, Type, ExternalLink, LayoutGrid, X, ChevronDown, History, Star, Quote,
  Home as HomeIcon, Package, User, MessageCircle, Mail, Eye, EyeOff,
} from "lucide-react";
import { api, LOGO_URL, COMPANY } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { updateContentCache } from "@/hooks/useContent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

/* ---------------- Editable primitives ---------------- */

function EditableText({ value, onChange, multiline, className, style, placeholder = "Click to edit", testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const ref = useRef(null);

  useEffect(() => { setDraft(value ?? ""); }, [value]);
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      if (!multiline) ref.current.setSelectionRange(0, ref.current.value.length);
    }
  }, [editing, multiline]);

  const commit = () => {
    setEditing(false);
    if (draft !== value) onChange(draft);
  };

  if (editing) {
    return multiline ? (
      <textarea
        ref={ref}
        data-testid={testid ? `${testid}-input` : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Escape") { setDraft(value); setEditing(false); } }}
        className={`bg-white/95 border-2 border-[#D9381E] rounded-md p-2 outline-none w-full ${className || ""}`}
        style={style}
        rows={4}
      />
    ) : (
      <input
        ref={ref}
        data-testid={testid ? `${testid}-input` : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(value); setEditing(false); } }}
        className={`bg-white/95 border-2 border-[#D9381E] rounded-md p-1 outline-none w-full ${className || ""}`}
        style={style}
      />
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      data-testid={testid}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") setEditing(true); }}
      className={`relative inline-block group cursor-text hover:outline hover:outline-2 hover:outline-dashed hover:outline-[#D9381E] hover:outline-offset-2 rounded px-0.5 ${className || ""}`}
      style={style}
    >
      {value || <span className="text-black/30 italic">{placeholder}</span>}
      <span className="opacity-0 group-hover:opacity-100 absolute -top-3 -right-2 bg-[#D9381E] text-white rounded-full h-5 w-5 flex items-center justify-center text-[10px] pointer-events-none">
        <Pencil size={10} />
      </span>
    </span>
  );
}

function EditableImage({ src, onChange, onClickLibrary, alt = "", className = "", label = "banner" }) {
  const inputRef = useRef(null);
  return (
    <div className={`relative group ${className}`} data-testid={`editable-image-${label}`}>
      <img src={src} alt={alt} className="w-full h-full object-contain" data-testid={`editable-image-img-${label}`} />
      <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 group-hover:bg-black/40 transition-colors">
        <Button type="button" size="sm" data-testid={`upload-${label}`} onClick={() => inputRef.current?.click()} className="opacity-0 group-hover:opacity-100 bg-white text-[#111] hover:bg-[#F2A900] rounded-full">
          <ImageIcon size={14} className="mr-1" /> Upload
        </Button>
        <Button type="button" size="sm" data-testid={`library-${label}`} onClick={onClickLibrary} className="opacity-0 group-hover:opacity-100 bg-white text-[#111] hover:bg-[#F2A900] rounded-full">
          <LayoutGrid size={14} className="mr-1" /> Library
        </Button>
      </div>
      <input
        ref={inputRef}
        data-testid={`upload-input-${label}`}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onChange(f); e.target.value = ""; }}
      />
    </div>
  );
}

/* ---------------- Visibility helpers ---------------- */

function parseHidden(raw) {
  if (!raw) return new Set();
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.map(String));
  } catch { return new Set(); }
}

function AtomEye({ id, hiddenSet, onToggle, className = "", label }) {
  const isHiddenAtom = hiddenSet.has(id);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onToggle(id); }}
      data-testid={`eye-${id}`}
      aria-label={isHiddenAtom ? `Show ${label || id}` : `Hide ${label || id}`}
      title={isHiddenAtom ? "Currently hidden — click to show" : "Visible — click to hide"}
      className={`inline-flex items-center justify-center h-6 w-6 rounded-full border transition-colors ${
        isHiddenAtom
          ? "bg-[#D9381E] text-white border-[#D9381E]"
          : "bg-white/90 text-[#111] border-black/10 hover:bg-[#F2A900]"
      } ${className}`}
    >
      {isHiddenAtom ? <EyeOff size={12} /> : <Eye size={12} />}
    </button>
  );
}

function AtomWrap({ id, hiddenSet, onToggle, label, children, className = "" }) {
  const hidden = hiddenSet.has(id);
  return (
    <span className={`relative inline-flex items-center gap-1 ${hidden ? "opacity-40" : ""} ${className}`}>
      {children}
      <AtomEye id={id} hiddenSet={hiddenSet} onToggle={onToggle} label={label} />
    </span>
  );
}

function SectionEye({ id, hiddenSet, onToggle, label }) {
  const hidden = hiddenSet.has(id);
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      data-testid={`section-eye-${id}`}
      title={hidden ? "Section is hidden — click to show" : "Section is visible — click to hide"}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border shadow-sm ${
        hidden
          ? "bg-[#D9381E] text-white border-[#D9381E]"
          : "bg-white text-[#111] border-black/10 hover:bg-[#F2A900]"
      }`}
    >
      {hidden ? <EyeOff size={12} /> : <Eye size={12} />}
      {hidden ? "Hidden" : "Visible"}
      <span className="hidden sm:inline text-black/50 font-normal">· {label || id}</span>
    </button>
  );
}

function ItemEye({ hidden, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      aria-label={hidden ? "Show item" : "Hide item"}
      title={hidden ? "Hidden — click to show" : "Visible — click to hide"}
      className={`absolute -top-2 -left-2 h-6 w-6 rounded-full flex items-center justify-center shadow border ${
        hidden
          ? "bg-[#D9381E] text-white border-[#D9381E]"
          : "bg-white text-[#111] border-black/10 hover:bg-[#F2A900]"
      }`}
    >
      {hidden ? <EyeOff size={11} /> : <Eye size={11} />}
    </button>
  );
}


/* ---------------- Page ---------------- */

function EditableStats({ raw, onChange }) {
  const parseStats = (r) => {
    if (!r) return [];
    try {
      const arr = JSON.parse(r);
      return Array.isArray(arr) ? arr.map((s) => ({ n: s?.n ?? "", l: s?.l ?? "", hidden: !!s?.hidden })) : [];
    } catch { return []; }
  };
  const [stats, setStats] = useState(parseStats(raw));
  useEffect(() => { setStats(parseStats(raw)); }, [raw]);

  const commit = (next) => {
    setStats(next);
    onChange(JSON.stringify(next));
  };
  const updateLocal = (idx, field, val) => {
    setStats((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: val } : s)));
  };
  const commitLocal = () => commit(stats);
  const removeItem = (idx) => commit(stats.filter((_, i) => i !== idx));
  const toggleHiddenItem = (idx) => commit(stats.map((s, i) => (i === idx ? { ...s, hidden: !s.hidden } : s)));
  const addItem = () => commit([...stats, { n: "0", l: "New Metric", hidden: false }]);

  return (
    <div className="mt-10 max-w-md" data-testid="editable-stats">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">Stats / metrics card</div>
        <Button
          type="button"
          size="sm"
          onClick={addItem}
          data-testid="stats-add"
          className="rounded-full bg-[#111] hover:bg-black text-[#F2A900] h-8 px-3 text-xs"
        >
          <Plus size={12} className="mr-1" /> Add metric
        </Button>
      </div>
      {stats.length === 0 ? (
        <div className="p-4 rounded-xl border-2 border-dashed border-black/20 text-sm text-[#6B7280] bg-white/50">
          No metrics yet. Click <span className="font-bold">Add metric</span> to create one.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`stat-row-${i}`}
              className={`relative p-3 rounded-xl bg-white border border-black/10 shadow-sm group ${s.hidden ? "opacity-50" : ""}`}
            >
              <ItemEye hidden={s.hidden} onToggle={() => toggleHiddenItem(i)} testid={`stat-hide-${i}`} />
              <button
                type="button"
                onClick={() => removeItem(i)}
                data-testid={`stat-delete-${i}`}
                aria-label="Delete metric"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#D9381E] text-white flex items-center justify-center shadow hover:bg-[#B02C15]"
              >
                <X size={12} />
              </button>
              <Input
                value={s.n}
                onChange={(e) => updateLocal(i, "n", e.target.value)}
                onBlur={commitLocal}
                data-testid={`stat-n-${i}`}
                placeholder="9+"
                className="font-display font-black text-2xl h-10 px-2 border-black/10"
              />
              <Input
                value={s.l}
                onChange={(e) => updateLocal(i, "l", e.target.value)}
                onBlur={commitLocal}
                data-testid={`stat-l-${i}`}
                placeholder="Yrs Experience"
                className="mt-2 text-xs h-8 px-2 border-black/10 uppercase tracking-widest text-[#6B7280]"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ICON_CHOICES = [
  "leaf", "award", "truck", "sparkles", "heart", "factory", "shield", "star", "zap", "graduationcap",
];

const FOOTER_ICON_CHOICES = ["", "mappin", "phone", "mail", "external"];

function EditableUsp({ raw, onChange }) {
  const parse = (r) => {
    if (!r) return [];
    try {
      const a = JSON.parse(r);
      if (!Array.isArray(a)) return [];
      return a.map((s) => {
        if (typeof s === "string") return { text: s, hidden: false };
        return { text: String(s?.text ?? s?.value ?? ""), hidden: !!s?.hidden };
      });
    }
    catch { return []; }
  };
  const [items, setItems] = useState(parse(raw));
  useEffect(() => { setItems(parse(raw)); }, [raw]);

  const commit = (next) => { setItems(next); onChange(JSON.stringify(next)); };
  const updateLocal = (idx, val) => setItems((prev) => prev.map((s, i) => (i === idx ? { ...s, text: val } : s)));
  const commitLocal = () => commit(items);
  const removeItem = (idx) => commit(items.filter((_, i) => i !== idx));
  const toggleHiddenItem = (idx) => commit(items.map((s, i) => (i === idx ? { ...s, hidden: !s.hidden } : s)));
  const addItem = () => commit([...items, { text: "NEW USP", hidden: false }]);

  return (
    <div data-testid="editable-usp">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900]">USP scrolling strip</div>
        <Button
          type="button"
          size="sm"
          onClick={addItem}
          data-testid="usp-add"
          className="rounded-full bg-[#F2A900] hover:bg-[#e09b00] text-[#111] h-8 px-3 text-xs"
        >
          <Plus size={12} className="mr-1" /> Add phrase
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="p-4 rounded-xl border-2 border-dashed border-white/30 text-sm text-white/60">
          No USP phrases yet. Click <span className="font-bold text-[#F2A900]">Add phrase</span> to create one.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((s, i) => (
            <div
              key={i}
              data-testid={`usp-row-${i}`}
              className={`relative flex items-center bg-white/10 border border-white/20 rounded-full pl-8 pr-9 py-1.5 group ${s.hidden ? "opacity-50" : ""}`}
            >
              <ItemEye hidden={s.hidden} onToggle={() => toggleHiddenItem(i)} testid={`usp-hide-${i}`} />
              <input
                value={s.text}
                onChange={(e) => updateLocal(i, e.target.value)}
                onBlur={commitLocal}
                data-testid={`usp-input-${i}`}
                className="bg-transparent outline-none text-white font-display font-black text-lg uppercase tracking-wider min-w-[8ch]"
                style={{ width: `${Math.max(String(s.text || "").length + 1, 8)}ch` }}
              />
              <button
                type="button"
                onClick={() => removeItem(i)}
                data-testid={`usp-delete-${i}`}
                aria-label="Delete USP"
                className="absolute right-1.5 h-6 w-6 rounded-full bg-[#D9381E] text-white flex items-center justify-center shadow hover:bg-[#B02C15]"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditableFeatures({ raw, onChange }) {
  const parse = (r) => {
    if (!r) return [];
    try {
      const a = JSON.parse(r);
      return Array.isArray(a) ? a.map((f) => ({
        icon: f?.icon || "sparkles",
        title: f?.title || "",
        text: f?.text || "",
        hidden: !!f?.hidden,
      })) : [];
    } catch { return []; }
  };
  const [items, setItems] = useState(parse(raw));
  useEffect(() => { setItems(parse(raw)); }, [raw]);

  const commit = (next) => { setItems(next); onChange(JSON.stringify(next)); };
  const updateLocal = (idx, field, val) => setItems((prev) => prev.map((f, i) => (i === idx ? { ...f, [field]: val } : f)));
  const commitLocal = () => commit(items);
  const commitField = (idx, field, val) => {
    const next = items.map((f, i) => (i === idx ? { ...f, [field]: val } : f));
    commit(next);
  };
  const removeItem = (idx) => commit(items.filter((_, i) => i !== idx));
  const toggleHiddenItem = (idx) => commit(items.map((f, i) => (i === idx ? { ...f, hidden: !f.hidden } : f)));
  const addItem = () => commit([...items, { icon: "sparkles", title: "New Feature", text: "Describe why this matters.", hidden: false }]);

  return (
    <div className="mt-10" data-testid="editable-features">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">Feature cards</div>
        <Button
          type="button"
          size="sm"
          onClick={addItem}
          data-testid="feature-add"
          className="rounded-full bg-[#111] hover:bg-black text-[#F2A900] h-8 px-3 text-xs"
        >
          <Plus size={12} className="mr-1" /> Add feature
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="p-4 rounded-xl border-2 border-dashed border-black/20 text-sm text-[#6B7280] bg-white/50">
          No features yet. Click <span className="font-bold">Add feature</span> to create one.
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-4">
          {items.map((f, i) => (
            <div
              key={i}
              data-testid={`feature-row-${i}`}
              className={`relative p-6 bg-[#FDFBF7] rounded-2xl border border-black/10 group ${f.hidden ? "opacity-50" : ""}`}
            >
              <ItemEye hidden={f.hidden} onToggle={() => toggleHiddenItem(i)} testid={`feature-hide-${i}`} />
              <button
                type="button"
                onClick={() => removeItem(i)}
                data-testid={`feature-delete-${i}`}
                aria-label="Delete feature"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#D9381E] text-white flex items-center justify-center shadow hover:bg-[#B02C15]"
              >
                <X size={12} />
              </button>
              <select
                value={f.icon}
                onChange={(e) => commitField(i, "icon", e.target.value)}
                data-testid={`feature-icon-${i}`}
                className="h-9 w-full text-xs uppercase tracking-widest border border-black/10 rounded-lg bg-white px-2"
              >
                {ICON_CHOICES.map((ic) => (<option key={ic} value={ic}>{ic}</option>))}
              </select>
              <Input
                value={f.title}
                onChange={(e) => updateLocal(i, "title", e.target.value)}
                onBlur={commitLocal}
                data-testid={`feature-title-${i}`}
                placeholder="Title"
                className="mt-3 font-display font-bold text-lg h-10 px-2 border-black/10"
              />
              <Textarea
                value={f.text}
                onChange={(e) => updateLocal(i, "text", e.target.value)}
                onBlur={commitLocal}
                data-testid={`feature-text-${i}`}
                placeholder="Description"
                rows={3}
                className="mt-2 text-sm px-2 border-black/10 resize-none"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function EditableFooterColumns({ raw, onChange }) {
  const parse = (r) => {
    if (!r) return [];
    try {
      const a = JSON.parse(r);
      return Array.isArray(a) ? a.map((c) => ({
        title: c?.title || "",
        hidden: !!c?.hidden,
        items: Array.isArray(c?.items) ? c.items.map((i) => ({
          label: i?.label || "",
          href: i?.href || "",
          icon: i?.icon || "",
          hidden: !!i?.hidden,
        })) : [],
      })) : [];
    } catch { return []; }
  };
  const [cols, setCols] = useState(parse(raw));
  useEffect(() => { setCols(parse(raw)); }, [raw]);

  const commit = (next) => { setCols(next); onChange(JSON.stringify(next)); };
  const commitLocal = () => commit(cols);

  const setColTitle = (ci, val) => setCols((prev) => prev.map((c, i) => (i === ci ? { ...c, title: val } : c)));
  const toggleColHidden = (ci) => commit(cols.map((c, i) => (i === ci ? { ...c, hidden: !c.hidden } : c)));
  const toggleItemHidden = (ci, ii) => commit(cols.map((c, i) => i === ci ? { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, hidden: !it.hidden } : it)) } : c));
  const setItemField = (ci, ii, field, val) =>
    setCols((prev) => prev.map((c, i) => i === ci ? { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, [field]: val } : it)) } : c));
  const commitItemIconChange = (ci, ii, val) => {
    const next = cols.map((c, i) => i === ci ? { ...c, items: c.items.map((it, j) => (j === ii ? { ...it, icon: val } : it)) } : c);
    commit(next);
  };
  const removeItem = (ci, ii) => commit(cols.map((c, i) => i === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c));
  const addItem = (ci) => commit(cols.map((c, i) => i === ci ? { ...c, items: [...c.items, { label: "New link", href: "/", icon: "", hidden: false }] } : c));
  const removeCol = (ci) => commit(cols.filter((_, i) => i !== ci));
  const addCol = () => commit([...cols, { title: "New column", hidden: false, items: [{ label: "New link", href: "/", icon: "", hidden: false }] }]);

  return (
    <div data-testid="editable-footer-columns">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900]">Link columns</div>
        <Button
          type="button"
          size="sm"
          onClick={addCol}
          data-testid="footer-add-col"
          className="rounded-full bg-[#F2A900] hover:bg-[#e09b00] text-[#111] h-8 px-3 text-xs"
        >
          <Plus size={12} className="mr-1" /> Add column
        </Button>
      </div>

      {cols.length === 0 ? (
        <div className="p-4 rounded-xl border-2 border-dashed border-white/30 text-sm text-white/60">
          No columns yet. Click <span className="font-bold text-[#F2A900]">Add column</span> to create one.
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {cols.map((col, ci) => (
            <div
              key={ci}
              data-testid={`footer-col-editor-${ci}`}
              className={`relative p-5 rounded-2xl bg-white/5 border border-white/10 ${col.hidden ? "opacity-50" : ""}`}
            >
              <ItemEye hidden={col.hidden} onToggle={() => toggleColHidden(ci)} testid={`footer-col-hide-${ci}`} />
              <button
                type="button"
                onClick={() => removeCol(ci)}
                data-testid={`footer-col-delete-${ci}`}
                aria-label="Delete column"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#D9381E] text-white flex items-center justify-center shadow hover:bg-[#B02C15]"
              >
                <X size={12} />
              </button>
              <Input
                value={col.title}
                onChange={(e) => setColTitle(ci, e.target.value)}
                onBlur={commitLocal}
                data-testid={`footer-col-title-${ci}`}
                placeholder="Column title"
                className="text-xs uppercase tracking-widest font-bold h-9 px-2 bg-white/10 border-white/20 text-[#F2A900] placeholder:text-white/40"
              />
              <div className="mt-4 space-y-3">
                {col.items.map((it, ii) => (
                  <div
                    key={ii}
                    data-testid={`footer-item-editor-${ci}-${ii}`}
                    className={`relative p-3 rounded-lg bg-white/5 border border-white/10 space-y-2 ${it.hidden ? "opacity-50" : ""}`}
                  >
                    <ItemEye hidden={it.hidden} onToggle={() => toggleItemHidden(ci, ii)} testid={`footer-item-hide-${ci}-${ii}`} />
                    <button
                      type="button"
                      onClick={() => removeItem(ci, ii)}
                      data-testid={`footer-item-delete-${ci}-${ii}`}
                      aria-label="Delete item"
                      className="absolute top-2 right-2 h-6 w-6 rounded-full bg-[#D9381E] text-white flex items-center justify-center hover:bg-[#B02C15]"
                    >
                      <X size={11} />
                    </button>
                    <div className="pr-9">
                      <Input
                        value={it.label}
                        onChange={(e) => setItemField(ci, ii, "label", e.target.value)}
                        onBlur={commitLocal}
                        data-testid={`footer-item-label-${ci}-${ii}`}
                        placeholder="Label (text shown)"
                        className="h-9 px-2 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      />
                    </div>
                    <Input
                      value={it.href}
                      onChange={(e) => setItemField(ci, ii, "href", e.target.value)}
                      onBlur={commitLocal}
                      data-testid={`footer-item-href-${ci}-${ii}`}
                      placeholder="Link (leave blank for plain text) — /route, https://…, tel:…, mailto:…"
                      className="h-9 px-2 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    />
                    <select
                      value={it.icon}
                      onChange={(e) => commitItemIconChange(ci, ii, e.target.value)}
                      data-testid={`footer-item-icon-${ci}-${ii}`}
                      className="h-9 w-full text-xs uppercase tracking-widest border border-white/20 rounded-md bg-white/10 px-2 text-white"
                    >
                      {FOOTER_ICON_CHOICES.map((ic) => (<option key={ic || "none"} value={ic} className="text-[#111]">{ic || "no icon"}</option>))}
                    </select>
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  onClick={() => addItem(ci)}
                  data-testid={`footer-add-item-${ci}`}
                  className="w-full rounded-full bg-white/10 hover:bg-white/20 text-white h-8 text-xs border border-white/20"
                >
                  <Plus size={12} className="mr-1" /> Add link/text
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const CONTACT_ICON_CHOICES = ["mappin","phone","mail","building","filecheck","external","sparkles","heart","award","truck","leaf","graduationcap","shield","star","zap"];
const CONTACT_TONE_CHOICES = [
  { v: "red", l: "Red icon tile" },
  { v: "gold", l: "Gold icon tile" },
  { v: "dark", l: "Dark icon tile" },
  { v: "cream", l: "Cream compact card" },
];

function EditableContactCards({ raw, onChange }) {
  const parse = (r) => {
    if (!r) return [];
    try {
      const a = JSON.parse(r);
      return Array.isArray(a) ? a.map((c) => ({
        icon: c?.icon || "sparkles",
        eyebrow: c?.eyebrow || "",
        value: c?.value || "",
        href: c?.href || "",
        cta: c?.cta || "",
        tone: c?.tone || "red",
        hidden: !!c?.hidden,
      })) : [];
    } catch { return []; }
  };
  const [cards, setCards] = useState(parse(raw));
  useEffect(() => { setCards(parse(raw)); }, [raw]);

  const commit = (next) => { setCards(next); onChange(JSON.stringify(next)); };
  const commitLocal = () => commit(cards);
  const setField = (i, k, v) => setCards((prev) => prev.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const setFieldAndCommit = (i, k, v) => {
    const next = cards.map((c, idx) => (idx === i ? { ...c, [k]: v } : c));
    commit(next);
  };
  const remove = (i) => commit(cards.filter((_, idx) => idx !== i));
  const toggleHiddenItem = (i) => commit(cards.map((c, idx) => (idx === i ? { ...c, hidden: !c.hidden } : c)));
  const add = () => commit([...cards, { icon: "sparkles", eyebrow: "New card", value: "Info", href: "", cta: "", tone: "red", hidden: false }]);

  return (
    <div data-testid="editable-contact-cards">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">Contact info cards</div>
        <Button type="button" size="sm" onClick={add} data-testid="contact-card-add" className="rounded-full bg-[#111] hover:bg-black text-[#F2A900] h-8 px-3 text-xs">
          <Plus size={12} className="mr-1" /> Add card
        </Button>
      </div>
      {cards.length === 0 ? (
        <div className="p-4 rounded-xl border-2 border-dashed border-black/20 text-sm text-[#6B7280] bg-white/50">
          No cards yet. Click <span className="font-bold">Add card</span> to create one.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {cards.map((card, i) => (
            <div key={i} data-testid={`contact-card-editor-${i}`} className={`relative p-5 rounded-2xl bg-white border border-black/10 ${card.hidden ? "opacity-50" : ""}`}>
              <ItemEye hidden={card.hidden} onToggle={() => toggleHiddenItem(i)} testid={`contact-card-hide-${i}`} />
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`contact-card-delete-${i}`}
                aria-label="Delete card"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#D9381E] text-white flex items-center justify-center shadow hover:bg-[#B02C15]"
              >
                <X size={12} />
              </button>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={card.icon}
                  onChange={(e) => setFieldAndCommit(i, "icon", e.target.value)}
                  data-testid={`contact-card-icon-${i}`}
                  className="h-9 w-full text-xs uppercase tracking-widest border border-black/10 rounded-lg bg-white px-2"
                >
                  {CONTACT_ICON_CHOICES.map((ic) => (<option key={ic} value={ic}>{ic}</option>))}
                </select>
                <select
                  value={card.tone}
                  onChange={(e) => setFieldAndCommit(i, "tone", e.target.value)}
                  data-testid={`contact-card-tone-${i}`}
                  className="h-9 w-full text-xs border border-black/10 rounded-lg bg-white px-2"
                >
                  {CONTACT_TONE_CHOICES.map((t) => (<option key={t.v} value={t.v}>{t.l}</option>))}
                </select>
              </div>
              <Input
                value={card.eyebrow}
                onChange={(e) => setField(i, "eyebrow", e.target.value)}
                onBlur={commitLocal}
                data-testid={`contact-card-eyebrow-${i}`}
                placeholder="Eyebrow (e.g. Corporate Address)"
                className="mt-3 h-9 px-2 text-xs uppercase tracking-widest border-black/10"
              />
              <Textarea
                value={card.value}
                onChange={(e) => setField(i, "value", e.target.value)}
                onBlur={commitLocal}
                data-testid={`contact-card-value-${i}`}
                placeholder="Main text / value shown on the card"
                rows={2}
                className="mt-2 text-sm px-2 border-black/10 resize-none"
              />
              <Input
                value={card.href}
                onChange={(e) => setField(i, "href", e.target.value)}
                onBlur={commitLocal}
                data-testid={`contact-card-href-${i}`}
                placeholder="Optional link — https://…, tel:…, mailto:…"
                className="mt-2 h-9 px-2 text-xs border-black/10"
              />
              <Input
                value={card.cta}
                onChange={(e) => setField(i, "cta", e.target.value)}
                onBlur={commitLocal}
                data-testid={`contact-card-cta-${i}`}
                placeholder='Optional CTA text (e.g. "Tap to call →")'
                className="mt-2 h-9 px-2 text-xs border-black/10"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const FIELD_TYPE_CHOICES = ["text", "email", "phone", "number", "textarea", "select", "checkbox"];

const VISIBILITY_CATALOG = [
  { label: "Home — Hero", items: [
    { id: "home.hero.section", label: "Entire hero section" },
    { id: "home.hero.tagline", label: "Tagline pill" },
    { id: "home.hero.title_1", label: "Title line 1" },
    { id: "home.hero.title_2", label: "Title highlight" },
    { id: "home.hero.title_3", label: "Title line 3" },
    { id: "home.hero.subtitle", label: "Subtitle" },
    { id: "home.hero.cta_primary", label: "Primary CTA button" },
    { id: "home.hero.cta_secondary", label: "Secondary CTA button" },
    { id: "home.hero.stats", label: "Stats card row" },
    { id: "home.hero.image", label: "Hero image" },
  ]},
  { label: "Home — USP marquee", items: [
    { id: "home.usp.section", label: "USP scrolling strip" },
  ]},
  { label: "Home — Products", items: [
    { id: "home.products.section", label: "Products preview section" },
    { id: "home.products.eyebrow", label: "Eyebrow text" },
    { id: "home.products.title", label: "Section title" },
  ]},
  { label: "Home — Why B-Raw", items: [
    { id: "home.why.section", label: "Entire Why section" },
    { id: "home.why.title", label: "Section title" },
  ]},
  { label: "About", items: [
    { id: "about.story.section", label: "Story section" },
    { id: "about.title", label: "Main heading" },
    { id: "about.para_1", label: "Paragraph 1" },
    { id: "about.para_2", label: "Paragraph 2" },
    { id: "about.para_3", label: "Paragraph 3" },
    { id: "about.para_4", label: "Paragraph 4" },
    { id: "about.milestones.section", label: "Milestone cards row" },
    { id: "about.promise.section", label: "Promise strip" },
    { id: "about.promise_eyebrow", label: "Promise eyebrow" },
    { id: "about.promise", label: "Promise quote" },
    { id: "about.promise_by", label: "Promise attribution" },
  ]},
  { label: "Reviews", items: [
    { id: "testimonials.section", label: "Entire reviews page" },
    { id: "testimonials.title", label: "Reviews heading" },
  ]},
  { label: "Contact", items: [
    { id: "contact.eyebrow", label: "Page eyebrow" },
    { id: "contact.title", label: "Page heading" },
    { id: "contact.cards.section", label: "Info-cards column" },
    { id: "contact.form.section", label: "Drop-us-a-message form" },
    { id: "contact.form.products", label: "Products-of-interest picker" },
  ]},
  { label: "Footer", items: [
    { id: "footer.section", label: "Entire footer" },
    { id: "footer.brand.section", label: "Brand blurb block" },
    { id: "footer.tagline", label: "Brand tagline text" },
    { id: "footer.columns.section", label: "Link columns block" },
    { id: "footer.copyright.section", label: "Copyright bottom bar" },
    { id: "footer.copyright", label: "Copyright text" },
    { id: "footer.rightline", label: "Right-side tagline" },
  ]},
];


function slugify(s) {
  return String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "field";
}

function EditableContactFormFields({ raw, onChange }) {
  const parse = (r) => {
    if (!r) return [];
    try {
      const a = JSON.parse(r);
      return Array.isArray(a) ? a.map((f, i) => ({
        key: (f?.key || `field_${i}`).toString(),
        label: f?.label || "",
        type: f?.type || "text",
        required: !!f?.required,
        colspan: Number(f?.colspan) === 2 ? 2 : 1,
        placeholder: f?.placeholder || "",
        options: f?.options || "",
        hidden: !!f?.hidden,
      })) : [];
    } catch { return []; }
  };
  const [fields, setFields] = useState(parse(raw));
  const [manualKey, setManualKey] = useState(() => new Set());
  useEffect(() => { setFields(parse(raw)); }, [raw]);

  const commit = (next) => { setFields(next); onChange(JSON.stringify(next)); };
  const commitLocal = () => commit(fields);
  const setField = (i, k, v) => setFields((prev) => prev.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));
  const setFieldAndCommit = (i, k, v) => {
    const next = fields.map((f, idx) => (idx === i ? { ...f, [k]: v } : f));
    commit(next);
  };
  const remove = (i) => {
    commit(fields.filter((_, idx) => idx !== i));
    setManualKey((prev) => { const next = new Set(); prev.forEach((v) => { if (v < i) next.add(v); else if (v > i) next.add(v - 1); }); return next; });
  };
  const toggleHiddenItem = (i) => commit(fields.map((f, idx) => (idx === i ? { ...f, hidden: !f.hidden } : f)));
  const add = () => {
    const existingKeys = new Set(fields.map((f) => f.key));
    let base = "field";
    let k = base;
    let n = 1;
    while (existingKeys.has(k)) { n += 1; k = `${base}_${n}`; }
    commit([...fields, { key: k, label: "New field", type: "text", required: false, colspan: 1, placeholder: "", options: "", hidden: false }]);
  };

  return (
    <div data-testid="editable-form-fields">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900]">Form fields</div>
        <Button type="button" size="sm" onClick={add} data-testid="form-field-add" className="rounded-full bg-[#F2A900] hover:bg-[#e09b00] text-[#111] h-8 px-3 text-xs">
          <Plus size={12} className="mr-1" /> Add field
        </Button>
      </div>
      {fields.length === 0 ? (
        <div className="p-4 rounded-xl border-2 border-dashed border-white/30 text-sm text-white/60">
          No fields yet. Click <span className="font-bold text-[#F2A900]">Add field</span> to create one.
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {fields.map((f, i) => (
            <div key={i} data-testid={`form-field-editor-${i}`} className={`relative p-4 rounded-xl bg-white/5 border border-white/10 ${f.hidden ? "opacity-50" : ""}`}>
              <ItemEye hidden={f.hidden} onToggle={() => toggleHiddenItem(i)} testid={`form-field-hide-${i}`} />
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`form-field-delete-${i}`}
                aria-label="Delete field"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#D9381E] text-white flex items-center justify-center shadow hover:bg-[#B02C15]"
              >
                <X size={12} />
              </button>
              <Input
                value={f.label}
                onChange={(e) => setField(i, "label", e.target.value)}
                onBlur={() => {
                  // Auto-derive key from label unless the user manually customized the key
                  const next = fields.map((ff, idx) => {
                    if (idx !== i) return ff;
                    if (manualKey.has(i)) return ff;
                    return { ...ff, key: slugify(ff.label) };
                  });
                  commit(next);
                }}
                data-testid={`form-field-label-${i}`}
                placeholder="Label shown to user"
                className="h-9 px-2 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              />
              <Input
                value={f.key}
                onChange={(e) => {
                  setField(i, "key", slugify(e.target.value));
                  setManualKey((prev) => { const n = new Set(prev); n.add(i); return n; });
                }}
                onBlur={commitLocal}
                data-testid={`form-field-key-${i}`}
                placeholder="Internal key (auto)"
                className="mt-2 h-8 px-2 text-xs bg-white/10 border-white/20 text-white/70 placeholder:text-white/30"
              />
              <div className="mt-2 grid grid-cols-2 gap-2">
                <select
                  value={f.type}
                  onChange={(e) => setFieldAndCommit(i, "type", e.target.value)}
                  data-testid={`form-field-type-${i}`}
                  className="h-9 text-xs uppercase tracking-widest border border-white/20 rounded-md bg-white/10 text-white px-2"
                >
                  {FIELD_TYPE_CHOICES.map((t) => (<option key={t} value={t} className="text-[#111]">{t}</option>))}
                </select>
                <select
                  value={f.colspan}
                  onChange={(e) => setFieldAndCommit(i, "colspan", Number(e.target.value))}
                  data-testid={`form-field-colspan-${i}`}
                  className="h-9 text-xs border border-white/20 rounded-md bg-white/10 text-white px-2"
                >
                  <option value={1} className="text-[#111]">Half width</option>
                  <option value={2} className="text-[#111]">Full width</option>
                </select>
              </div>
              {f.type === "select" && (
                <Input
                  value={f.options}
                  onChange={(e) => setField(i, "options", e.target.value)}
                  onBlur={commitLocal}
                  data-testid={`form-field-options-${i}`}
                  placeholder="Options separated by | (e.g. Query | Bulk order | Feedback)"
                  className="mt-2 h-8 px-2 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/40"
                />
              )}
              {f.type !== "checkbox" && (
                <Input
                  value={f.placeholder}
                  onChange={(e) => setField(i, "placeholder", e.target.value)}
                  onBlur={commitLocal}
                  data-testid={`form-field-placeholder-${i}`}
                  placeholder="Placeholder text (optional)"
                  className="mt-2 h-8 px-2 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/40"
                />
              )}
              <label className="mt-3 flex items-center gap-2 text-xs text-white/80 cursor-pointer">
                <input
                  type="checkbox"
                  checked={f.required}
                  onChange={(e) => setFieldAndCommit(i, "required", e.target.checked)}
                  data-testid={`form-field-required-${i}`}
                  className="h-4 w-4 accent-[#F2A900]"
                />
                Required
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function AdminContentWysiwyg() {
  const nav = useNavigate();
  const { user, logout: doLogout } = useAuth();

  const [content, setContent] = useState({});           // DEFAULT + published + drafts merged
  const [publishedContent, setPublishedContent] = useState({});
  const [products, setProducts] = useState([]);          // merged (with drafts applied)
  const [publishedProducts, setPublishedProducts] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryTarget, setLibraryTarget] = useState(null); // {kind:'content', key} | {kind:'product', pid}
  const [addOpen, setAddOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);   // full product object being edited
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // pid
  const [publishing, setPublishing] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [publishedTestimonials, setPublishedTestimonials] = useState([]);
  const [addTestimonialOpen, setAddTestimonialOpen] = useState(false);
  const [editTestimonial, setEditTestimonial] = useState(null);
  const [confirmDeleteT, setConfirmDeleteT] = useState(null);
  const [section, setSection] = useState("home");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [publishHistory, setPublishHistory] = useState([]);

  const reload = async () => {
    try {
      const [c, p, t, d] = await Promise.all([
        api.get("/content"),
        api.get("/products"),
        api.get("/testimonials"),
        api.get("/draft"),
      ]);
      setPublishedContent(c.data);
      setPublishedProducts(p.data);
      setPublishedTestimonials(t.data);
      setDrafts(d.data);
    } catch (err) {
      if (err?.response?.status === 401) nav("/admin/login");
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/publish-history");
      setPublishHistory(data);
    } catch {}
  };

  useEffect(() => {
    if (user === false) nav("/admin/login");
    if (user) reload();
  }, [user]);

  // Compute merged view (published + draft)
  useEffect(() => {
    const c = { ...publishedContent };
    let ps = publishedProducts.map((p) => ({ ...p }));
    let ts = publishedTestimonials.map((t) => ({ ...t }));
    for (const d of drafts) {
      if (d.kind === "content") c[d.target] = d.value.value;
      else if (d.kind === "product_update") {
        ps = ps.map((p) => (p.id === d.target ? { ...p, ...d.value, _dirty: true } : p));
      } else if (d.kind === "product_delete") {
        ps = ps.map((p) => (p.id === d.target ? { ...p, _deleted: true } : p));
      } else if (d.kind === "product_create") {
        ps.push({
          id: d.target, ...d.value, allergen: "New — please review",
          nutrition: {}, _staged: true,
        });
      } else if (d.kind === "testimonial_update") {
        ts = ts.map((t) => (t.id === d.target ? { ...t, ...d.value, _dirty: true } : t));
      } else if (d.kind === "testimonial_delete") {
        ts = ts.map((t) => (t.id === d.target ? { ...t, _deleted: true } : t));
      } else if (d.kind === "testimonial_create") {
        ts.push({ id: d.target, ...d.value, _staged: true });
      }
    }
    setContent(c);
    setProducts(ps);
    setTestimonials(ts);
  }, [publishedContent, publishedProducts, publishedTestimonials, drafts]);

  const dirtyCount = drafts.length;

  /* ---------------- Actions ---------------- */

  const stageContent = async (key, value) => {
    if ((publishedContent[key] ?? "") === value) {
      // If value returns to published, drop the draft
      const has = drafts.find((d) => d.kind === "content" && d.target === key);
      if (has) {
        try { await api.delete(`/draft/entry/content/${key}`); } catch {}
      }
      await reload();
      return;
    }
    try {
      await api.put(`/draft/content/${key}`, { value });
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const stageProduct = async (pid, patch) => {
    try {
      await api.put(`/draft/products/${pid}`, patch);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const stageProductDelete = async (pid) => {
    try {
      await api.delete(`/draft/products/${pid}`);
      await reload();
      toast.success("Marked for deletion");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const undeleteProduct = async (pid) => {
    try {
      await api.delete(`/draft/entry/product_delete/${pid}`);
      await reload();
    } catch {}
  };

  const stageProductCreate = async (payload) => {
    try {
      const { data } = await api.post("/draft/products", payload);
      await reload();
      toast.success("New product staged");
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const uploadImage = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/uploads/image", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const openLibrary = async (target) => {
    setLibraryTarget(target);
    try {
      const { data } = await api.get("/uploads");
      setLibrary(data);
    } catch {}
    setLibraryOpen(true);
  };

  const pickFromLibrary = async (url) => {
    if (!libraryTarget) return;
    if (libraryTarget.kind === "content") await stageContent(libraryTarget.key, url);
    else if (libraryTarget.kind === "product") await stageProduct(libraryTarget.pid, { image: url });
    setLibraryOpen(false);
  };

  const handleHeroImageUpload = async (file) => {
    try {
      const url = await uploadImage(file);
      await stageContent("home.hero.image", url);
      toast.success("Hero image staged");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  const handleProductImageUpload = async (pid, file) => {
    try {
      const url = await uploadImage(file);
      await stageProduct(pid, { image: url });
    } catch { toast.error("Upload failed"); }
  };

  const discardAll = async () => {
    try {
      await api.delete("/draft");
      await reload();
      setConfirmDiscard(false);
      toast.success("Draft discarded");
    } catch { toast.error("Discard failed"); }
  };

  const stageTestimonial = async (tid, patch) => {
    try { await api.put(`/draft/testimonials/${tid}`, patch); await reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const stageTestimonialCreate = async (payload) => {
    try { const { data } = await api.post("/draft/testimonials", payload); await reload(); toast.success("New review staged"); return data; }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const stageTestimonialDelete = async (tid) => {
    try { await api.delete(`/draft/testimonials/${tid}`); await reload(); toast.success("Marked for deletion"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const undeleteTestimonial = async (tid) => {
    try { await api.delete(`/draft/entry/testimonial_delete/${tid}`); await reload(); } catch {}
  };

  const revertPublish = async (rec) => {
    if (!window.confirm(`Revert publish from ${new Date(rec.at).toLocaleString()}? This will restore the previous values.`)) return;
    try {
      await api.post(`/publish-history/${rec.id}/revert`);
      toast.success("Publish reverted");
      await reload();
      await loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revert failed");
    }
  };

  const publishAll = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/draft/publish");
      if (data.failures?.length) {
        toast.error(`Published ${data.total}, but ${data.failures.length} failed.`);
      } else {
        toast.success(`Published ${data.total} change${data.total === 1 ? "" : "s"}`);
      }
      Object.keys(content).forEach((k) => updateContentCache(k, content[k]));
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const doLogoutAndGo = async () => { await doLogout(); nav("/admin/login"); };

  /* ---------------- Layout ---------------- */

  const T = (k, fb) => content[k] ?? fb;
  const heroImage = T("home.hero.image", "/braw-logo.png");
  const visibleProducts = products.filter((p) => !p._deleted);
  const featured = visibleProducts.slice(0, 3);

  const hiddenSet = useMemo(() => parseHidden(T("hidden.blocks", "[]")), [content]);
  const toggleHidden = (id) => {
    const next = new Set(hiddenSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    stageContent("hidden.blocks", JSON.stringify(Array.from(next)));
  };
  const eyeProps = { hiddenSet, onToggle: toggleHidden };

  return (
    <div className="min-h-screen bg-[#F3EAD3]/40 pb-32">
      {/* Sticky editor header */}
      <header className="border-b border-black/10 bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin" className="text-sm text-[#6B7280] hover:text-[#111] inline-flex items-center gap-1 shrink-0"><ArrowLeft size={14}/> Console</Link>
            <img src={LOGO_URL} alt="B-Raw" className="h-8 w-auto ml-2 shrink-0" />
            <div className="hidden sm:block truncate">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B7280]">Live Content Editor</div>
              <div className="text-xs text-[#6B7280]">Click any text · hover images/cards to edit</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { loadHistory(); setHistoryOpen(true); }}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 bg-white text-xs font-bold text-[#111] hover:bg-[#F3EAD3]"
              data-testid="open-history"
            >
              <History size={14}/> Undo history
            </button>
            <button
              onClick={() => setVisibilityOpen(true)}
              data-testid="open-visibility"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 bg-white text-xs font-bold text-[#111] hover:bg-[#F3EAD3]"
            >
              <Eye size={14}/> Visibility {hiddenSet.size > 0 && <span className="ml-1 inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-[#D9381E] text-white text-[10px] font-bold">{hiddenSet.size}</span>}
            </button>
            <Link to="/admin/content/text" className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 bg-white text-xs font-bold text-[#111] hover:bg-[#F3EAD3]">
              <Type size={14}/> Text-only mode
            </Link>
            <a href="/" target="_blank" rel="noreferrer" className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 bg-white text-xs font-bold text-[#111] hover:bg-[#F3EAD3]">
              <ExternalLink size={14}/> View live site
            </a>
          </div>
        </div>
        {/* Section tabs */}
        <div className="border-t border-black/5 bg-[#FDFBF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex gap-1 overflow-x-auto" data-testid="section-tabs">
            {[
              { k: "home", l: "Home", icon: HomeIcon },
              { k: "products", l: "Products", icon: Package },
              { k: "about", l: "About", icon: User },
              { k: "reviews", l: "Reviews", icon: MessageCircle },
              { k: "contact", l: "Contact", icon: Mail },
              { k: "footer", l: "Footer", icon: LayoutGrid },
            ].map(({ k, l, icon: Icon }) => (
              <button
                key={k}
                onClick={() => setSection(k)}
                data-testid={`tab-${k}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold rounded-full whitespace-nowrap ${section === k ? "bg-[#111] text-[#F2A900]" : "text-[#111] hover:bg-black/5"}`}
              >
                <Icon size={12}/> {l}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Editor canvas — mirrors public Home */}
      <div className="relative">
        {loading ? (
          <div className="p-10 text-center text-[#6B7280]">Loading editor…</div>
        ) : (
          <>
            {/* HERO */}
            {section === "home" && (
            <section className="relative overflow-hidden sunburst-bg">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-24 grid md:grid-cols-12 gap-8 items-center">
                <div className="md:col-span-7">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#111] text-[#F2A900] text-xs font-bold uppercase tracking-[0.2em] rounded-full">
                    <EditableText testid="et-home.hero.tagline" value={T("home.hero.tagline")} onChange={(v) => stageContent("home.hero.tagline", v)} />
                  </div>
                  <h1 className="mt-5 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-[#111]">
                    <EditableText testid="et-home.hero.title_1" value={T("home.hero.title_1")} onChange={(v) => stageContent("home.hero.title_1", v)} />
                    {" "}
                    <EditableText testid="et-home.hero.title_2" value={T("home.hero.title_2")} onChange={(v) => stageContent("home.hero.title_2", v)} style={{ color: "#D9381E" }} />
                    <br />
                    <EditableText testid="et-home.hero.title_3" value={T("home.hero.title_3")} onChange={(v) => stageContent("home.hero.title_3", v)} />
                  </h1>
                  <p className="mt-6 text-base md:text-lg text-[#4B5563] max-w-xl leading-relaxed">
                    <EditableText testid="et-home.hero.subtitle" multiline value={T("home.hero.subtitle")} onChange={(v) => stageContent("home.hero.subtitle", v)} />
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <span className={`relative inline-flex items-center gap-2 bg-[#D9381E] text-white px-6 py-3.5 rounded-full font-bold cursor-default ${hiddenSet.has("home.hero.cta_primary") ? "opacity-40" : ""}`}>
                      <AtomEye id="home.hero.cta_primary" {...eyeProps} label="Primary CTA" className="!absolute -top-2 -left-2" />
                      <EditableText testid="et-home.hero.cta_primary" value={T("home.hero.cta_primary")} onChange={(v) => stageContent("home.hero.cta_primary", v)} />
                    </span>
                    <span className={`relative inline-flex items-center gap-2 border-2 border-[#111] text-[#111] px-6 py-3.5 rounded-full font-bold cursor-default ${hiddenSet.has("home.hero.cta_secondary") ? "opacity-40" : ""}`}>
                      <AtomEye id="home.hero.cta_secondary" {...eyeProps} label="Secondary CTA" className="!absolute -top-2 -left-2" />
                      <EditableText testid="et-home.hero.cta_secondary" value={T("home.hero.cta_secondary")} onChange={(v) => stageContent("home.hero.cta_secondary", v)} />
                    </span>
                  </div>
                  <EditableStats
                    raw={T("home.hero.stats")}
                    onChange={(json) => stageContent("home.hero.stats", json)}
                  />
                </div>

                <div className="md:col-span-5 relative">
                  <div className="absolute inset-0 -z-10 spin-slow opacity-40">
                    <div className="w-72 h-72 sm:w-96 sm:h-96 rounded-full bg-gradient-to-tr from-[#F2A900] to-[#D9381E] mx-auto" />
                  </div>
                  <EditableImage
                    src={heroImage}
                    onChange={handleHeroImageUpload}
                    onClickLibrary={() => openLibrary({ kind: "content", key: "home.hero.image" })}
                    alt="Hero"
                    className="w-full max-w-md mx-auto aspect-square drop-shadow-[0_20px_40px_rgba(217,56,30,0.25)]"
                    label="Hero"
                  />
                </div>
              </div>
            </section>
            )}

            {/* USP MARQUEE */}
            {section === "home" && (
            <section className="bg-[#111] text-[#FDFBF7] py-8">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <EditableUsp
                  raw={T("home.usp.marquee")}
                  onChange={(json) => stageContent("home.usp.marquee", json)}
                />
              </div>
            </section>
            )}

            {/* PRODUCTS */}
            {(section === "home" || section === "products") && (
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">
                    <EditableText testid="et-home.products.eyebrow" value={T("home.products.eyebrow")} onChange={(v) => stageContent("home.products.eyebrow", v)} />
                  </div>
                  <h2 className="mt-2 font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#111]">
                    <EditableText testid="et-home.products.title" value={T("home.products.title")} onChange={(v) => stageContent("home.products.title", v)} />
                  </h2>
                </div>
                <Button onClick={() => setAddOpen(true)} data-testid="add-product-btn" className="bg-[#D9381E] hover:bg-[#B02C15] rounded-full">
                  <Plus size={16} className="mr-2" /> Add product
                </Button>
              </div>

              <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {visibleProducts.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onEdit={() => setEditProduct(p)}
                    onDelete={() => setConfirmDelete(p)}
                    onUpload={(f) => handleProductImageUpload(p.id, f)}
                    onLibrary={() => openLibrary({ kind: "product", pid: p.id })}
                  />
                ))}
                {products.filter((p) => p._deleted).map((p) => (
                  <div key={p.id} className="rounded-3xl border-2 border-dashed border-[#D9381E] p-6 bg-[#FEE2E2] flex flex-col items-center justify-center text-center">
                    <Trash2 className="text-[#D9381E]" />
                    <div className="mt-3 font-display font-bold text-[#111]">{p.name}</div>
                    <div className="text-xs text-[#6B7280] mt-1">Marked for deletion</div>
                    <Button onClick={() => undeleteProduct(p.id)} data-testid={`undo-delete-${p.id}`} variant="outline" size="sm" className="mt-3 rounded-full border-[#D9381E] text-[#D9381E]">
                      <Undo2 size={12} className="mr-1"/> Undo
                    </Button>
                  </div>
                ))}
              </div>
            </section>
            )}

            {/* WHY */}
            {section === "home" && (
            <section className="bg-[#F3EAD3] py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">
                    Why B-Raw
                  </div>
                  <h2 className="mt-2 font-display font-bold text-4xl sm:text-5xl tracking-tight text-[#111]">
                    <EditableText testid="et-home.why.title" value={T("home.why.title")} onChange={(v) => stageContent("home.why.title", v)} />
                  </h2>
                </div>
                <EditableFeatures
                  raw={T("home.why.features")}
                  onChange={(json) => stageContent("home.why.features", json)}
                />
              </div>
            </section>
            )}

            {/* REVIEWS PAGE */}
            {section === "reviews" && (
              <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="flex items-end justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">Real Reviews</div>
                    <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tighter text-[#111]">
                      <EditableText value={T("testimonials.title")} onChange={(v) => stageContent("testimonials.title", v)} />
                    </h2>
                  </div>
                  <Button onClick={() => setAddTestimonialOpen(true)} data-testid="add-testimonial-btn" className="bg-[#D9381E] hover:bg-[#B02C15] rounded-full">
                    <Plus size={16} className="mr-2" /> Add review
                  </Button>
                </div>
                <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {testimonials.filter((t) => !t._deleted).map((t, i) => (
                    <div key={t.id} className="group relative p-6 rounded-3xl border border-black/10"
                         style={{ backgroundColor: i % 3 === 0 ? "#FFEDD5" : i % 3 === 1 ? "#DBEAFE" : "#DCFCE7" }}
                         data-testid={`wysiwyg-testimonial-${t.id}`}>
                      {(t._dirty || t._staged) && <Badge className="absolute top-3 left-3 z-10 bg-[#F2A900] text-[#111] hover:bg-[#F2A900]">{t._staged ? "New" : "Edited"}</Badge>}
                      <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <button onClick={() => setEditTestimonial(t)} className="h-8 w-8 rounded-full bg-white text-[#111] hover:bg-[#F2A900] shadow flex items-center justify-center" data-testid={`edit-testimonial-${t.id}`}><Pencil size={14} /></button>
                        <button onClick={() => setConfirmDeleteT(t)} className="h-8 w-8 rounded-full bg-white text-[#D9381E] hover:bg-[#FEE2E2] shadow flex items-center justify-center" data-testid={`delete-testimonial-${t.id}`}><Trash2 size={14} /></button>
                      </div>
                      <Quote className="text-[#D9381E]" size={28} />
                      <div className="mt-3 flex">
                        {Array.from({ length: 5 }).map((_, idx) => (
                          <Star key={idx} size={16} className={idx < (t.rating || 0) ? "fill-[#F2A900] text-[#F2A900]" : "text-black/20"} />
                        ))}
                      </div>
                      <p className="mt-4 text-[#111] leading-relaxed">{t.text}</p>
                      <div className="mt-6 flex items-center justify-between">
                        <div>
                          <div className="font-display font-bold text-[#111]">{t.name}</div>
                          <div className="text-xs text-[#4B5563]">{t.location}</div>
                        </div>
                        <div className="text-xs font-bold uppercase tracking-widest text-[#111]/60">{t.product}</div>
                      </div>
                    </div>
                  ))}
                  {testimonials.filter((t) => t._deleted).map((t) => (
                    <div key={t.id} className="rounded-3xl border-2 border-dashed border-[#D9381E] p-6 bg-[#FEE2E2] flex flex-col items-center justify-center text-center">
                      <Trash2 className="text-[#D9381E]" />
                      <div className="mt-3 font-display font-bold text-[#111]">{t.name}</div>
                      <div className="text-xs text-[#6B7280] mt-1">Marked for deletion</div>
                      <Button onClick={() => undeleteTestimonial(t.id)} variant="outline" size="sm" className="mt-3 rounded-full border-[#D9381E] text-[#D9381E]"><Undo2 size={12} className="mr-1"/> Undo</Button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* CONTACT PAGE */}
            {section === "contact" && (
              <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-10">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">
                    <EditableText testid="et-contact.eyebrow" value={T("contact.eyebrow")} onChange={(v) => stageContent("contact.eyebrow", v)} />
                  </div>
                  <h2 className="mt-2 font-display font-black text-5xl tracking-tighter text-[#111]">
                    <EditableText testid="et-contact.title" value={T("contact.title")} onChange={(v) => stageContent("contact.title", v)} />
                  </h2>
                </div>

                <EditableContactCards
                  raw={T("contact.cards")}
                  onChange={(json) => stageContent("contact.cards", json)}
                />

                <div className="p-6 sm:p-10 rounded-3xl bg-[#111] text-white">
                  <div className="mb-2">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900] mb-1">Form title</div>
                    <div className="font-display font-black text-3xl">
                      <EditableText testid="et-contact.form_title" value={T("contact.form_title")} onChange={(v) => stageContent("contact.form_title", v)} />
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900] mb-1">Form subtitle</div>
                    <div className="text-white/80 text-sm">
                      <EditableText testid="et-contact.form_subtitle" multiline value={T("contact.form_subtitle")} onChange={(v) => stageContent("contact.form_subtitle", v)} />
                    </div>
                  </div>
                  <EditableContactFormFields
                    raw={T("contact.form_fields")}
                    onChange={(json) => stageContent("contact.form_fields", json)}
                  />
                  <div className="mt-6">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900] mb-1">Submit button text</div>
                    <div className="inline-block bg-[#D9381E] text-white px-6 py-3 rounded-full font-bold">
                      <EditableText testid="et-contact.form_button" value={T("contact.form_button")} onChange={(v) => stageContent("contact.form_button", v)} />
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ABOUT */}
            {section === "about" && (
            <section className="bg-white py-16">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E]">About</div>
                <h2 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tight text-[#111]">
                  <EditableText testid="et-about.title" value={T("about.title")} onChange={(v) => stageContent("about.title", v)} />
                </h2>
                {["about.para_1","about.para_2","about.para_3","about.para_4"].map((k) => (
                  <p key={k} className="mt-5 text-[#4B5563] leading-relaxed">
                    <EditableText testid={`et-${k}`} multiline value={T(k)} onChange={(v) => stageContent(k, v)} />
                  </p>
                ))}
                <div className="mt-10">
                  <EditableFeatures
                    raw={T("about.milestones")}
                    onChange={(json) => stageContent("about.milestones", json)}
                  />
                </div>

                <div className="mt-12 bg-[#111] text-white rounded-3xl p-8 text-center">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900]">
                    <EditableText testid="et-about.promise_eyebrow" value={T("about.promise_eyebrow")} onChange={(v) => stageContent("about.promise_eyebrow", v)} />
                  </div>
                  <p className="mt-4 font-display text-2xl sm:text-3xl leading-tight">
                    &ldquo;<EditableText testid="et-about.promise" multiline value={T("about.promise")} onChange={(v) => stageContent("about.promise", v)} />&rdquo;
                  </p>
                  <div className="mt-4 text-white/70 text-sm">
                    <EditableText testid="et-about.promise_by" value={T("about.promise_by")} onChange={(v) => stageContent("about.promise_by", v)} />
                  </div>
                  <div className="mt-8 text-left">
                    <EditableFeatures
                      raw={T("about.promise_pillars")}
                      onChange={(json) => stageContent("about.promise_pillars", json)}
                    />
                  </div>
                </div>
              </div>
            </section>
            )}

            {/* FOOTER */}
            {section === "footer" && (
            <section className="bg-[#111] text-[#FDFBF7] py-16">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900] mb-2">Brand blurb</div>
                  <div className="text-sm text-white/80 max-w-2xl leading-relaxed">
                    <EditableText
                      testid="et-footer.tagline"
                      multiline
                      value={T("footer.tagline")}
                      onChange={(v) => stageContent("footer.tagline", v)}
                    />
                  </div>
                </div>

                <EditableFooterColumns
                  raw={T("footer.columns")}
                  onChange={(json) => stageContent("footer.columns", json)}
                />

                <div className="border-t border-white/10 pt-6 grid gap-4 md:grid-cols-2 items-center">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900] mb-1">Copyright line</div>
                    <div className="text-xs text-white/60">Use <code className="text-[#F2A900]">{"{year}"}</code> to auto-insert the current year.</div>
                    <div className="mt-2 text-sm">
                      <EditableText
                        testid="et-footer.copyright"
                        value={T("footer.copyright")}
                        onChange={(v) => stageContent("footer.copyright", v)}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F2A900] mb-1">Right-side tagline</div>
                    <div className="text-sm mt-2">
                      <EditableText
                        testid="et-footer.rightline"
                        value={T("footer.rightline")}
                        onChange={(v) => stageContent("footer.rightline", v)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
            )}
          </>
        )}
      </div>

      {/* Floating action bar */}
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 max-w-3xl w-[calc(100%-2rem)]">
        <div className="rounded-full bg-[#111] text-white shadow-[0_20px_60px_rgba(0,0,0,0.4)] px-4 sm:px-6 py-3 flex items-center gap-3 border border-white/10">
          <div className="shrink-0 h-9 w-9 rounded-full bg-[#F2A900] text-[#111] flex items-center justify-center font-bold text-sm" data-testid="pending-count">
            {dirtyCount}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold">{dirtyCount === 0 ? "All clean" : `${dirtyCount} pending change${dirtyCount === 1 ? "" : "s"}`}</div>
            <div className="text-[11px] text-white/60 truncate">
              {dirtyCount === 0 ? "Click any text or image above to start editing" : summarizeDrafts(drafts)}
            </div>
          </div>
          <Button
            variant="outline"
            disabled={dirtyCount === 0}
            onClick={() => setConfirmDiscard(true)}
            data-testid="discard-btn"
            className="border-white/20 text-white/90 hover:bg-white/10 hover:text-white rounded-full disabled:opacity-40 bg-transparent"
          >
            <Undo2 size={14} className="mr-2" /> Discard
          </Button>
          <Button
            disabled={dirtyCount === 0 || publishing}
            onClick={publishAll}
            data-testid="publish-btn"
            className="bg-[#F2A900] hover:bg-[#e09b00] text-[#111] rounded-full font-bold disabled:opacity-50"
          >
            <Send size={14} className="mr-2" /> {publishing ? "Publishing…" : "Publish"}
          </Button>
        </div>
      </div>

      {/* Image library dialog */}
      <Dialog open={libraryOpen} onOpenChange={setLibraryOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-display">Image library</DialogTitle></DialogHeader>
          {library.length === 0 ? (
            <div className="text-sm text-[#6B7280] p-6 text-center">No uploads yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto" data-testid="library-grid">
              {library.map((im) => (
                <button
                  key={im.id}
                  data-testid={`library-item-${im.id}`}
                  onClick={() => pickFromLibrary(im.url)}
                  className="group relative rounded-xl overflow-hidden border border-black/10 aspect-square bg-[#F3EAD3] hover:ring-2 hover:ring-[#D9381E]"
                >
                  <img src={im.url} alt={im.original_filename} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add product dialog */}
      <AddProductDialog open={addOpen} onOpenChange={setAddOpen} onCreate={async (payload) => { await stageProductCreate(payload); setAddOpen(false); }} onUpload={uploadImage} />

      {/* Edit product dialog */}
      <EditProductDialog product={editProduct} onClose={() => setEditProduct(null)} onSave={async (patch) => { if (editProduct) await stageProduct(editProduct.id, patch); setEditProduct(null); }} onUpload={uploadImage} />

      {/* Confirm discard */}
      <Dialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Discard all draft changes?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280]">This will throw away all {dirtyCount} unpublished change{dirtyCount === 1 ? "" : "s"} and reset the editor to what visitors currently see.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDiscard(false)}>Keep editing</Button>
            <Button onClick={discardAll} data-testid="discard-confirm" className="bg-[#D9381E] hover:bg-[#B02C15]">Discard all</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete product */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Remove {confirmDelete?.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280]">This stages the product for removal. It will only actually disappear from the site when you click <b>Publish</b>.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button onClick={async () => { if (confirmDelete) { await stageProductDelete(confirmDelete.id); setConfirmDelete(null); } }} data-testid="delete-confirm" className="bg-[#D9381E] hover:bg-[#B02C15]">Stage removal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Testimonial */}
      <TestimonialDialog
        open={addTestimonialOpen}
        onOpenChange={setAddTestimonialOpen}
        title="Add new review"
        initial={{ name: "", location: "", rating: 5, text: "", product: "" }}
        onSubmit={async (v) => { await stageTestimonialCreate(v); setAddTestimonialOpen(false); }}
      />
      <TestimonialDialog
        open={!!editTestimonial}
        onOpenChange={(o) => !o && setEditTestimonial(null)}
        title={`Edit review by ${editTestimonial?.name || ""}`}
        initial={editTestimonial}
        onSubmit={async (v) => { if (editTestimonial) await stageTestimonial(editTestimonial.id, v); setEditTestimonial(null); }}
      />
      <Dialog open={!!confirmDeleteT} onOpenChange={(o) => !o && setConfirmDeleteT(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Remove review by {confirmDeleteT?.name}?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6B7280]">This stages the review for removal. It will only actually disappear when you click <b>Publish</b>.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteT(null)}>Cancel</Button>
            <Button onClick={async () => { if (confirmDeleteT) { await stageTestimonialDelete(confirmDeleteT.id); setConfirmDeleteT(null); } }} data-testid="delete-testimonial-confirm" className="bg-[#D9381E] hover:bg-[#B02C15]">Stage removal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Visibility panel — universal hide/unhide for every atom & section */}
      <Dialog open={visibilityOpen} onOpenChange={setVisibilityOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Eye size={18}/> Visibility controls</DialogTitle>
            <p className="text-xs text-[#6B7280]">Toggle any block off to hide it from the public site. Click Publish to make hides go live. Currently hidden: {hiddenSet.size}</p>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto space-y-4 pr-2" data-testid="visibility-panel">
            {VISIBILITY_CATALOG.map((group) => (
              <div key={group.label}>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D9381E] mb-2">{group.label}</div>
                <div className="grid gap-1.5">
                  {group.items.map((it) => {
                    const on = !hiddenSet.has(it.id);
                    return (
                      <label
                        key={it.id}
                        data-testid={`vis-row-${it.id}`}
                        className={`flex items-center justify-between gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                          on ? "bg-white border-black/10 hover:border-black/30" : "bg-[#FEE2E2] border-[#D9381E]/40"
                        }`}
                      >
                        <div className="flex items-center gap-2 text-sm">
                          {on ? <Eye size={14} className="text-emerald-600" /> : <EyeOff size={14} className="text-[#D9381E]" />}
                          <span className={`${on ? "text-[#111]" : "text-[#D9381E] line-through"} font-semibold`}>{it.label}</span>
                          <span className="text-[10px] uppercase tracking-widest text-[#6B7280] hidden sm:inline">{it.id}</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleHidden(it.id)}
                          data-testid={`vis-toggle-${it.id}`}
                          className="h-4 w-4 accent-[#D9381E]"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <div className="text-xs text-[#6B7280] mr-auto">Hides are staged as drafts. Click <strong>Publish</strong> to push live.</div>
            <Button onClick={() => setVisibilityOpen(false)} variant="outline" className="rounded-full">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Publish history / Undo timeline */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><History size={18}/> Recent publishes</DialogTitle></DialogHeader>
          {publishHistory.length === 0 ? (
            <div className="text-sm text-[#6B7280] p-6 text-center">No publish history yet. Once you Publish a change, it will show up here for one-tap undo.</div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="history-list">
              {publishHistory.map((h) => (
                <div key={h.id} className={`p-4 rounded-2xl border ${h.reverted ? "bg-[#F3EAD3] border-[#F2A900]/40" : "bg-white border-black/10"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-[#6B7280]">{new Date(h.at).toLocaleString()} · {h.actor}</div>
                      <div className="mt-1 text-sm font-bold text-[#111]">{h.changes.length} change{h.changes.length === 1 ? "" : "s"}</div>
                      <div className="text-xs text-[#6B7280] mt-1 flex flex-wrap gap-1">
                        {Object.entries(h.totals || {}).filter(([_,v]) => v > 0).map(([k, v]) => (
                          <Badge key={k} variant="secondary" className="text-[10px]">{k.replace("_", " ")}: {v}</Badge>
                        ))}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => revertPublish(h)}
                      disabled={h.reverted}
                      data-testid={`revert-${h.id}`}
                      className="rounded-full border-[#D9381E] text-[#D9381E] shrink-0 disabled:opacity-40"
                    >
                      <Undo2 size={12} className="mr-1"/> {h.reverted ? "Reverted" : "Revert"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TestimonialDialog({ open, onOpenChange, title, initial, onSubmit }) {
  const [form, setForm] = useState(initial || { name: "", location: "", rating: 5, text: "", product: "" });
  useEffect(() => { setForm(initial || { name: "", location: "", rating: 5, text: "", product: "" }); }, [initial, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Reviewer name</Label>
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tf-name" className="mt-1 h-12" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Location</Label>
            <Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="tf-location" className="mt-1 h-12" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Rating (1-5)</Label>
            <Input type="number" min="1" max="5" value={form.rating ?? 5} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) || 5 })} data-testid="tf-rating" className="mt-1 h-12" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Review text</Label>
            <Textarea rows={4} value={form.text || ""} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="tf-text" className="mt-1" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Product mentioned</Label>
            <Input value={form.product || ""} onChange={(e) => setForm({ ...form, product: e.target.value })} data-testid="tf-product" className="mt-1 h-12" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => { if (!form.name?.trim() || !form.text?.trim()) return toast.error("Name and review text required"); onSubmit(form); }}
            data-testid="testimonial-submit"
            className="bg-[#D9381E] hover:bg-[#B02C15]"
          >
            Stage review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Sub-components ---------------- */

function ProductCard({ product, onEdit, onDelete, onUpload, onLibrary }) {
  const inputRef = useRef(null);
  const dirty = product._dirty || product._staged;
  return (
    <div
      className="group relative rounded-3xl overflow-hidden border border-black/10"
      style={{ backgroundColor: product.bg || "#FFEDD5" }}
      data-testid={`wysiwyg-product-${product.id}`}
    >
      {dirty && (
        <Badge className="absolute top-3 left-3 z-10 bg-[#F2A900] text-[#111] hover:bg-[#F2A900]">
          {product._staged ? "New (staged)" : "Edited"}
        </Badge>
      )}
      <div className="absolute top-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
        <button onClick={onEdit} title="Edit" className="h-8 w-8 rounded-full bg-white text-[#111] hover:bg-[#F2A900] shadow flex items-center justify-center" data-testid={`edit-${product.id}`}>
          <Pencil size={14} />
        </button>
        <button onClick={onDelete} title="Remove" className="h-8 w-8 rounded-full bg-white text-[#D9381E] hover:bg-[#FEE2E2] shadow flex items-center justify-center" data-testid={`delete-${product.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
      <div className="p-6 pb-0">
        <div className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: product.color }}>
          {product.price} pouch
        </div>
        <h3 className="mt-2 font-display font-bold text-3xl tracking-tight" style={{ color: product.color }}>
          {product.name}
        </h3>
        <p className="mt-2 text-sm text-black/60 line-clamp-2">{product.tagline}</p>
      </div>
      <div className="aspect-[4/3] relative">
        <img src={product.image || "/braw-logo.png"} alt={product.name} className="w-full h-full object-contain object-bottom p-4" />
        <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/0 group-hover:bg-black/30 transition-colors">
          <Button size="sm" data-testid={`upload-${product.id}`} onClick={() => inputRef.current?.click()} className="opacity-0 group-hover:opacity-100 bg-white text-[#111] hover:bg-[#F2A900] rounded-full">
            <ImageIcon size={14} className="mr-1" /> Upload
          </Button>
          <Button size="sm" data-testid={`library-btn-${product.id}`} onClick={onLibrary} className="opacity-0 group-hover:opacity-100 bg-white text-[#111] hover:bg-[#F2A900] rounded-full">
            <LayoutGrid size={14} className="mr-1" /> Library
          </Button>
        </div>
        <input ref={inputRef} data-testid={`upload-input-${product.id}`} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
      </div>
    </div>
  );
}

function ProductForm({ value, onChange, onUpload }) {
  const inputRef = useRef(null);
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Name</Label>
        <Input value={value.name || ""} onChange={(e) => onChange({ ...value, name: e.target.value })} data-testid="pf-name" className="mt-1 h-12" />
      </div>
      <div className="sm:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Tagline</Label>
        <Input value={value.tagline || ""} onChange={(e) => onChange({ ...value, tagline: e.target.value })} data-testid="pf-tagline" className="mt-1 h-12" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Price</Label>
        <Input value={value.price || ""} onChange={(e) => onChange({ ...value, price: e.target.value })} data-testid="pf-price" className="mt-1 h-12" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Image</Label>
        <div className="mt-1 flex items-center gap-2">
          <img src={value.image || "/braw-logo.png"} alt="" className="h-12 w-12 rounded-md object-contain border border-black/10 bg-[#F3EAD3]" />
          <Button type="button" onClick={() => inputRef.current?.click()} variant="outline" size="sm" className="rounded-full border-black/10">
            <ImageIcon size={12} className="mr-1"/> Change
          </Button>
          <input ref={inputRef} data-testid="pf-image-input" type="file" accept="image/*" className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0]; if (!f) return;
              try {
                const url = await onUpload(f);
                onChange({ ...value, image: url });
              } catch { toast.error("Upload failed"); }
              e.target.value = "";
            }} />
        </div>
      </div>
      <div className="sm:col-span-2">
        <Label className="text-xs uppercase tracking-widest text-[#6B7280]">Ingredients / description</Label>
        <Textarea rows={4} value={value.ingredients || ""} onChange={(e) => onChange({ ...value, ingredients: e.target.value })} data-testid="pf-ingredients" className="mt-1" />
      </div>
    </div>
  );
}

function AddProductDialog({ open, onOpenChange, onCreate, onUpload }) {
  const [form, setForm] = useState({ name: "", tagline: "", price: "₹5", ingredients: "", image: "" });
  useEffect(() => { if (!open) setForm({ name: "", tagline: "", price: "₹5", ingredients: "", image: "" }); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Add new product</DialogTitle></DialogHeader>
        <ProductForm value={form} onChange={setForm} onUpload={onUpload} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => { if (!form.name.trim()) return toast.error("Name is required"); onCreate(form); }}
            data-testid="create-product"
            className="bg-[#D9381E] hover:bg-[#B02C15]"
          >
            <Plus size={14} className="mr-2" /> Stage new product
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditProductDialog({ product, onClose, onSave, onUpload }) {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(product ? { ...product } : null); }, [product]);
  if (!form) return null;
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Edit {product?.name}</DialogTitle></DialogHeader>
        <ProductForm value={form} onChange={setForm} onUpload={onUpload} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave({
              name: form.name, tagline: form.tagline, price: form.price,
              ingredients: form.ingredients, image: form.image,
            })}
            data-testid="save-product-edit"
            className="bg-[#D9381E] hover:bg-[#B02C15]"
          >
            <Save size={14} className="mr-2" /> Stage changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function summarizeDrafts(drafts) {
  const c = drafts.filter((d) => d.kind === "content").length;
  const pu = drafts.filter((d) => d.kind === "product_update").length;
  const pc = drafts.filter((d) => d.kind === "product_create").length;
  const pd = drafts.filter((d) => d.kind === "product_delete").length;
  const parts = [];
  if (c) parts.push(`${c} text`);
  if (pu) parts.push(`${pu} product edit${pu === 1 ? "" : "s"}`);
  if (pc) parts.push(`${pc} new`);
  if (pd) parts.push(`${pd} removed`);
  return parts.join(" · ") || "—";
}
